import { Link, useNavigate } from 'react-router';

import { NoticeCard } from '@/components/feedback/notice-card';
import { PlanCard } from '@/components/pricing/plan-card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth0 } from '@/context';
import { usePlans } from '@/hooks/use-plans';
import { subscribeAccountHref } from '@/lib/onboarding-funnel';
import { RUNTIME_CONFIG } from '@/lib/runtime-config';
import { rememberSelectedPlan } from '@/lib/selected-plan';

/**
 * Public pricing (funnel v2, ESCLOUD-332). Plans come from the public catalogue
 * endpoint on the SPA's default-region API: every region seeds the same
 * catalogue, so no region choice is needed to show a price. Choosing a plan
 * remembers it for the Auth0 round trip and either opens the account step (a
 * signed-in visitor, e.g. creating a second workspace) or starts Auth0 signup
 * with the account step as `returnTo`.
 */
export function PricingPage() {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();
    const { data, isLoading, isError, refetch } = usePlans();
    const paymentsWired = RUNTIME_CONFIG.stripePublishableKey !== '';

    const choose = (planId: string) => {
        rememberSelectedPlan(planId);
        const returnTo = subscribeAccountHref(planId);
        if (isAuthenticated) {
            navigate(returnTo);
            return;
        }
        loginWithRedirect({ authorizationParams: { screen_hint: 'signup' }, appState: { returnTo } });
    };

    const plans = [...(data?.items ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

    return (
        <div className="mx-auto w-full max-w-[940px] py-8">
            <div className="mb-7 flex items-start justify-between gap-6">
                <div>
                    <h1 className="m-0 text-[28px] font-semibold tracking-[-0.02em] text-foreground">
                        Plans
                    </h1>
                    <p className="mt-1.5 mb-0 max-w-[66ch] text-[15px] text-muted-foreground">
                        Prices per month, excluding tax. You choose your data region during subscription.
                    </p>
                </div>
                {!isAuthenticated && !authLoading && (
                    <Button asChild variant="link" className="h-auto shrink-0 p-0 text-sm">
                        <Link to="/login">Sign in</Link>
                    </Button>
                )}
            </div>

            {isLoading ? (
                <div className="grid gap-4 md:grid-cols-3" data-testid="plans-loading">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-[360px] w-full rounded-xl" />
                    ))}
                </div>
            ) : isError ? (
                <NoticeCard
                    title="Plans could not be loaded"
                    body="We could not reach the catalogue just now. This is usually temporary."
                    action={
                        <Button type="button" variant="outline" onClick={() => void refetch()}>
                            Try again
                        </Button>
                    }
                />
            ) : (
                <div className="grid items-stretch gap-4 md:grid-cols-3">
                    {plans.map((plan) => (
                        <PlanCard
                            key={plan.id}
                            plan={plan}
                            paymentsWired={paymentsWired}
                            disabled={authLoading}
                            onChoose={() => choose(plan.id)}
                        />
                    ))}
                </div>
            )}

            <p className="mt-5 mb-0 text-[13px] text-muted-foreground">
                ESCloud is intended for qualified healthcare professionals. Prices exclude tax and may vary by
                country.
            </p>
        </div>
    );
}
